/** Fluent behavior configuration over shared builder state. */

import { ActionContext } from './types';

import type { VFXConfig, BuffConfig } from './AbilityBuilderTypes';

import { AOEConfig } from './behaviors/AOEBehavior';
import { DashConfig } from './behaviors/DashBehavior';
import { ChannelConfig } from './behaviors/ChannelBehavior';
import { HealConfig } from './behaviors/HealBehavior';
import { ShieldConfig } from './behaviors/ShieldBehavior';
import { DebuffConfig } from './behaviors/DebuffBehavior';
import { MeleeConfig } from './behaviors/MeleeBehavior';
import { HitscanConfig } from './behaviors/HitscanBehavior';
import { SummonConfig } from './behaviors/SummonBehavior';
import type { AuraEffectConfig } from './behaviors/AuraEffectBehavior';
import { NovaProjectileConfig } from './behaviors/NovaProjectileBehavior';

import type { HitEffect } from './modifiers/types';
import { addProc } from './DamageModifiers';

import { type CasterAuraConfig } from './AbilityBuilderFactory';
import type { OrbitGeometry } from '../../../effects/castGeometry/castGeometry';

import { AbilityBuilderCore } from './AbilityBuilderCore';

export class AbilityBuilderBehaviors extends AbilityBuilderCore {
  withVFX(vfx: string | VFXConfig): this {
    if (typeof vfx === 'string') {
      this.projectileVFX = { effectId: vfx };
    } else {
      this.projectileVFX = vfx;
    }
    return this;
  }

  // ---------------------------------------------------------------------------
  // Instant Effect Configuration
  // ---------------------------------------------------------------------------

  withInstantEffect(effect: (ctx: ActionContext) => void): this {
    this.instantEffect = effect;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Buff/Debuff Configuration
  // ---------------------------------------------------------------------------

  withBuff(config: BuffConfig): this {
    this.buffConfigs.push(config);
    return this;
  }

  // ---------------------------------------------------------------------------
  // AOE Configuration
  // ---------------------------------------------------------------------------

  withAOE(config: AOEConfig): this {
    this.aoeConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Dash Configuration
  // ---------------------------------------------------------------------------

  withDash(config: DashConfig): this {
    this.dashConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Channel Configuration
  // ---------------------------------------------------------------------------

  withChannel(config: ChannelConfig): this {
    this.channelConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Heal Configuration
  // ---------------------------------------------------------------------------

  withHeal(config: HealConfig): this {
    this.healConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Shield Configuration
  // ---------------------------------------------------------------------------

  withShield(config: ShieldConfig): this {
    this.shieldConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Debuff Configuration
  // ---------------------------------------------------------------------------

  withDebuff(config: DebuffConfig): this {
    this.debuffConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Melee Configuration
  // ---------------------------------------------------------------------------

  /** Proper melee attack with arc rendering and range check */
  withMelee(config: MeleeConfig): this {
    this.meleeConfig = config;
    this.metadata.melee = {
      arcAngle: config.arcAngle,
      arcSpread: config.arcSpread,
      arcRadius: config.range,
      hitRange: config.range,
      knockback: config.knockback,
    };
    return this;
  }

  // ---------------------------------------------------------------------------
  // Hitscan Configuration
  // ---------------------------------------------------------------------------

  /** Instant-hit ranged attack with no projectile travel time */
  withHitscan(config: HitscanConfig): this {
    this.hitscanConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Summon Configuration
  // ---------------------------------------------------------------------------

  /** Spawn a persistent ground entity (trap, totem, zone) */
  withSummon(config: SummonConfig): this {
    this.summonConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Unified Damage & Range
  // ---------------------------------------------------------------------------

  /** Set damage used by all damage-dealing behaviors as default */
  withDamage(damage: number | ((ctx: ActionContext) => number)): this {
    this.metadata.damageFormula = damage;
    return this;
  }

  /** Set range used by UI tooltips and behaviors as default */
  withRange(range: number): this {
    this.metadata.range = range;
    return this;
  }

  /** Improved cast time with interrupt support */
  withCastTime(durationMs: number, options?: { interruptible?: boolean; movementCancels?: boolean; vfxId?: string }): this {
    this.windUpDuration = durationMs;
    this.metadata.castTime = durationMs;
    if (options?.vfxId) this.windUpVFX = options.vfxId;
    this.metadata.castOptions = options;
    return this;
  }

  /** Link this ability into a combo chain with the next ability */
  chainInto(nextAbilityId: string, windowMs?: number): this {
    this._chainInto = { abilityId: nextAbilityId, windowMs: windowMs ?? 2000 };
    this.metadata.chainInto = this._chainInto;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Projectile Trajectory
  // ---------------------------------------------------------------------------

  /** Set projectile flight pattern (sine wave, spiral, zigzag) */
  withTrajectory(type: 'sine' | 'spiral' | 'zigzag', amplitude?: number, frequency?: number): this {
    if (!this.metadata.projectile) this.metadata.projectile = { damage: 0, speed: 0 };
    this.metadata.projectile.trajectory = type;
    this.metadata.projectile.trajectoryAmplitude = amplitude;
    this.metadata.projectile.trajectoryFrequency = frequency;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Projectile Patterns
  // ---------------------------------------------------------------------------

  /** Fire N projectiles in a 360 degree circle from caster position */
  withNovaProjectiles(count: number, config: Omit<NovaProjectileConfig, 'count' | 'arcDegrees'>): this {
    this.novaConfig = { ...config, count, arcDegrees: 360 };
    return this;
  }

  /** Fire N projectiles in an arc in facing direction */
  withFanProjectiles(count: number, arcDegrees: number, config: Omit<NovaProjectileConfig, 'count' | 'arcDegrees'>): this {
    this.novaConfig = { ...config, count, arcDegrees };
    return this;
  }

  // ---------------------------------------------------------------------------
  // Passive Abilities
  // ---------------------------------------------------------------------------

  /** Register an always-active ability triggered by game events */
  withPassive(config: { trigger: string; chance?: number; cooldownMs?: number; effect: (ctx: ActionContext) => void }): this {
    this.passiveConfig = config;
    this.metadata.isPassive = true;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Toggle Abilities
  // ---------------------------------------------------------------------------

  /** Make this ability a toggle — press to activate, press again to deactivate */
  withToggle(config: { resourceDrainPerSecond?: number; onActivate?: (ctx: ActionContext) => void; onDeactivate?: (ctx: ActionContext) => void; maxDurationMs?: number }): this {
    this.toggleConfig = config;
    this.metadata.isToggle = true;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Charge System
  // ---------------------------------------------------------------------------

  /** Give this ability multiple charges that regenerate over time */
  withCharges(config: { maxCharges: number; regenTimeMs: number; consumePerCast?: number }): this {
    this.chargesConfig = { maxCharges: config.maxCharges, regenTimeMs: config.regenTimeMs, consumePerCast: config.consumePerCast ?? 1 };
    this.metadata.charges = this.chargesConfig;
    return this;
  }

  // ---------------------------------------------------------------------------
  // AOE Shapes
  // ---------------------------------------------------------------------------

  /** Cone-shaped AOE in facing direction */
  withConeAOE(config: { damage: number | ((ctx: ActionContext) => number); angle: number; distance: number; ticks?: number; vfxId?: string }): this {
    this.coneAOEConfig = config;
    return this;
  }

  /** Line-shaped AOE in facing direction */
  withLineAOE(config: { damage: number | ((ctx: ActionContext) => number); length: number; width: number; ticks?: number; vfxId?: string }): this {
    this.lineAOEConfig = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Proc Effects (delegates to DamageModifiers)
  // ---------------------------------------------------------------------------

  /** % chance to trigger an effect on a combat event */
  withProc(config: { trigger: 'onHit' | 'onCrit' | 'onDamageTaken' | 'onHeal'; chance: number; cooldownMs?: number; effect: HitEffect; vfxId?: string }): this {
    addProc(this.hitEffects, config);
    return this;
  }

  // ---------------------------------------------------------------------------
  // Damage Type
  // ---------------------------------------------------------------------------

  /** Classify damage as physical, magical, or true */
  withDamageType(type: 'physical' | 'magical' | 'true'): this {
    this.metadata.damageType = type;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Ground Targeting (Phase A — forward offset)
  // ---------------------------------------------------------------------------

  /** Place ability at a forward offset position instead of on the caster */
  withGroundTarget(config: { maxRange: number; indicatorRadius?: number; indicatorColor?: number }): this {
    this.metadata.groundTarget = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Ability Ranks
  // ---------------------------------------------------------------------------

  /** Define rank scaling for this ability */
  withRanks(ranks: Array<{ rank: number; damageMultiplier?: number; cooldownMs?: number; manaCost?: number; radiusBonus?: number }>): this {
    this.metadata.ranks = ranks;
    this.metadata.maxRank = ranks.length;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Secondary Resources
  // ---------------------------------------------------------------------------

  /** Set resource type and cost/generation for this ability */
  withResource(config: { type: string; cost?: number; generate?: number }): this {
    this.metadata.resource = config;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Aura Damage/Healing
  // ---------------------------------------------------------------------------

  /** Persistent aura that deals damage or heals in radius over time */
  withAuraEffect(config: AuraEffectConfig): this {
    // Store config for build() to create the behavior
    this._auraEffectConfig = config;
    return this;
  }

  /**
   * Character-anchored glow aura on the CASTER (S2.F3 / theme F) — purely
   * cosmetic (no damage). Emits a vfx:status_effect for the caster at
   * execute time → StatusVFXHandler → FollowerSystem keeps the glow on the
   * sprite center for durationMs, then removes it. The mechanism for
   * "attacks/abilities need character-anchored auras" (charge / heroic_leap
   * spin glow). effectId resolves against the registry or `${id}_aura`.
   */
  withCasterAura(config: CasterAuraConfig): this {
    this._casterAuraConfig = config;
    return this;
  }

  /**
   * Declare an orbit a custom instant effect draws (Lucky Seven), so the
   * cast geometry carries it to observers (plan 29 F5).
   */
  withCastOrbit(orbit: OrbitGeometry): this {
    this.metadata.castOrbit = orbit;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Composition API — clone configs from other registered abilities
  // ---------------------------------------------------------------------------

  /**
   * Clone projectile config from another registered ability (snapshot at build time).
   */

}
